'use client'

import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { selectCustomRangeApplied, selectTimeRangeValue } from '@apps/service-map/stores/service-map.selector'
import { useServiceMapStore } from '@apps/service-map/stores/service-map.store'

import { useCustomRangeState } from '../components/TimeRangeFilter/hooks/useCustomRangeState'
import type { ServiceMapTimeRangeFilterOption } from '../components/TimeRangeFilter/time-range.types'
import { combineDateTime } from '../components/TimeRangeFilter/utils/combineDateTime'
import { formatCustomRangeLabel } from '../components/TimeRangeFilter/utils/formatCustomRangeLabel'
import { toCustomRange } from '../components/TimeRangeFilter/utils/toCustomRange'
import { DEFAULT_TIME_RANGE_VALUE, TIME_RANGE_CUSTOM_VALUE } from '../service-map.const'
import type { ServiceMapTimeRangeFilterValue } from '../service-map.interface'
import { resolveCustomRangeFromTimeRangeSelection } from '../service-map.utils'

type UseMobileFiltersTimeRangeStateParams = {
  closeAllMenus: () => void
  customFallbackLabel: string
  customGroupRef: RefObject<HTMLDivElement | null>
  isOpen: boolean
  timeRangeOptions: readonly ServiceMapTimeRangeFilterOption[]
}

export function useMobileFiltersTimeRangeState({
  isOpen,
  customGroupRef,
  timeRangeOptions,
  customFallbackLabel,
  closeAllMenus,
}: UseMobileFiltersTimeRangeStateParams) {
  const timeRangeValue = useServiceMapStore(selectTimeRangeValue)
  const customRangeApplied = useServiceMapStore(selectCustomRangeApplied)
  const setTimeRangeFilter = useServiceMapStore((state) => state.setTimeRangeFilter)

  const timeRangeCustomRange = useMemo(() => {
    if (!customRangeApplied) {
      return undefined
    }

    return toCustomRange({
      startAt: new Date(customRangeApplied.startAt),
      endAt: new Date(customRangeApplied.endAt),
    })
  }, [customRangeApplied])

  const [pendingTimeRange, setPendingTimeRange] = useState<ServiceMapTimeRangeFilterValue>(timeRangeValue)
  const pendingScrollRef = useRef(false)

  const {
    range: pendingCustomRange,
    setRange,
    setStartDate,
    setStartTime,
    setEndDate,
    setEndTime,
  } = useCustomRangeState(timeRangeCustomRange)

  const selectedTimeRangeOption = useMemo(
    () => timeRangeOptions.find((option) => option.value === pendingTimeRange),
    [pendingTimeRange, timeRangeOptions]
  )

  const menuItems = useMemo(() => {
    const customIndex = timeRangeOptions.findIndex((option) => option.value === TIME_RANGE_CUSTOM_VALUE)
    const splitAt = customIndex >= 0 ? customIndex : timeRangeOptions.length
    return {
      regular: timeRangeOptions.slice(0, splitAt),
      custom: timeRangeOptions.slice(splitAt),
    }
  }, [timeRangeOptions])

  const showCustom = pendingTimeRange === TIME_RANGE_CUSTOM_VALUE

  const timeRangeTriggerLabel = useMemo(() => {
    if (pendingTimeRange === TIME_RANGE_CUSTOM_VALUE) {
      return formatCustomRangeLabel(pendingCustomRange) ?? customFallbackLabel
    }

    return selectedTimeRangeOption?.label ?? customFallbackLabel
  }, [customFallbackLabel, pendingCustomRange, pendingTimeRange, selectedTimeRangeOption?.label])

  const handleSelectTimeRange = useCallback(
    (value: ServiceMapTimeRangeFilterValue) => {
      if (value === TIME_RANGE_CUSTOM_VALUE && pendingTimeRange !== TIME_RANGE_CUSTOM_VALUE) {
        const migrated = resolveCustomRangeFromTimeRangeSelection(pendingTimeRange, pendingCustomRange)
        if (migrated) {
          setRange(migrated)
        }
        pendingScrollRef.current = true
      }
      setPendingTimeRange(value)
      closeAllMenus()
    },
    [closeAllMenus, pendingCustomRange, pendingTimeRange, setRange]
  )

  const resetPendingTimeRange = useCallback(() => {
    setPendingTimeRange(DEFAULT_TIME_RANGE_VALUE)
  }, [])

  const canApplyCustomRange = useMemo(() => {
    if (pendingTimeRange !== TIME_RANGE_CUSTOM_VALUE) return true
    const startAt = combineDateTime(pendingCustomRange.startDate, pendingCustomRange.startTime)
    const endAt = combineDateTime(pendingCustomRange.endDate, pendingCustomRange.endTime)
    return startAt !== null && endAt !== null && endAt.getTime() >= startAt.getTime()
  }, [pendingCustomRange, pendingTimeRange])

  const applyPendingTimeRangeToStore = useCallback((): boolean => {
    if (pendingTimeRange === TIME_RANGE_CUSTOM_VALUE) {
      const startAt = combineDateTime(pendingCustomRange.startDate, pendingCustomRange.startTime)
      const endAt = combineDateTime(pendingCustomRange.endDate, pendingCustomRange.endTime)
      if (!startAt || !endAt) return false
      setTimeRangeFilter(pendingTimeRange, { startAt: startAt.getTime(), endAt: endAt.getTime() })
      return true
    }

    setTimeRangeFilter(pendingTimeRange, null)
    return true
  }, [pendingCustomRange, pendingTimeRange, setTimeRangeFilter])

  useEffect(() => {
    if (isOpen) {
      setPendingTimeRange(timeRangeValue)
      if (timeRangeCustomRange) {
        setRange(timeRangeCustomRange)
      }
    }
  }, [isOpen, setRange, timeRangeCustomRange, timeRangeValue])

  useEffect(() => {
    if (pendingScrollRef.current && pendingTimeRange === TIME_RANGE_CUSTOM_VALUE) {
      customGroupRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      pendingScrollRef.current = false
    }
  }, [customGroupRef, pendingTimeRange])

  return {
    applyPendingTimeRangeToStore,
    canApplyCustomRange,
    handleSelectTimeRange,
    menuItems,
    pendingCustomRange,
    pendingTimeRange,
    resetPendingTimeRange,
    selectedTimeRangeOption,
    setEndDate,
    setEndTime,
    setStartDate,
    setStartTime,
    showCustom,
    timeRangeTriggerLabel,
  }
}
